
import { TrendingUp, Users, Music } from 'lucide-react';
import { InsightItem } from '../types/insightTypes';

let seed = 54321;

export const randomWithSeed = (min: number, max: number) => {
  const x = Math.sin(seed++) * 10000;
  return Math.floor((x - Math.floor(x)) * (max - min)) + min;
};

const getDataPoints = (timeRange: string) => {
  switch (timeRange) {
    case 'week':
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    case 'month':
      return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
    case 'year':
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    case 'all':
      return ['2020', '2021', '2022', '2023', '2024'];
    default:
      return ['Week 1', 'Week 2', 'Week 3', 'Week 4']; // Default to month view
  }
};

export const generateStreamingData = (timeRange: string = 'month') => {
  const dataPoints = getDataPoints(timeRange);
  return dataPoints.map(name => ({
    name,
    spotify: randomWithSeed(800, 2500),
    apple: randomWithSeed(500, 1800),
    other: randomWithSeed(300, 1200)
  }));
};

export const generateRevenueData = (timeRange: string = 'month') => {
  const dataPoints = getDataPoints(timeRange);
  return dataPoints.map(name => ({
    name,
    streams: randomWithSeed(40, 200),
    sync: randomWithSeed(20, 120),
    merch: randomWithSeed(10, 100)
  }));
};

export const generateDemographicData = () => {
  return [
    { name: 'US', value: 45 },
    { name: 'UK', value: 25 },
    { name: 'CA', value: 15 },
    { name: 'DE', value: 10 },
    { name: 'Other', value: 5 },
  ];
};

export const generatePlatformData = () => {
  return [
    { name: 'Spotify', value: 55 },
    { name: 'Apple Music', value: 30 },
    { name: 'YouTube', value: 10 },
    { name: 'Other', value: 5 },
  ];
};

export const generateAudienceMetrics = () => {
  return [
    { subject: 'Engagement', A: 75, fullMark: 100 },
    { subject: 'Retention', A: 65, fullMark: 100 },
    { subject: 'Growth', A: 80, fullMark: 100 },
    { subject: 'Conversion', A: 55, fullMark: 100 },
    { subject: 'Social Shares', A: 70, fullMark: 100 },
  ];
};

const getTimeRangeContext = (timeRange: string) => {
  switch (timeRange) {
    case 'week':
      return 'this week';
    case 'month':
      return 'this month';
    case 'year':
      return 'this year';
    case 'all':
      return 'overall';
    default:
      return 'this month';
  }
};

export const generateInsights = (timeRange: string = 'month'): InsightItem[] => {
  const context = getTimeRangeContext(timeRange);
  
  return [
    { 
      icon: TrendingUp, 
      title: 'Growth Opportunity', 
      content: `Your audience engagement is highest on Fridays ${context}. Consider releasing new content or scheduling posts on this day to maximize reach.`,
      detailedExplanation: `Analysis of your streaming and social media data shows a 32% higher engagement rate on Fridays between 4-8pm in your fans' local time zones ${context}. This presents a strategic opportunity to time releases and promotions for maximum impact.`
    },
    { 
      icon: Users, 
      title: 'Demographic Insight', 
      content: `Your music resonates strongly with listeners in urban areas aged 18-24 ${context}. Consider targeted promotion to this demographic for maximum impact.`,
      detailedExplanation: `Based on streaming location data and platform demographics ${context}, your core audience is concentrated in urban centers, particularly college towns. 67% of your most engaged listeners fall into the 18-24 age range, with interests in indie cinema, video games, and specialty coffee.`
    },
    { 
      icon: Music, 
      title: 'Content Strategy', 
      content: `Your acoustic tracks perform 32% better than electronic ones ${context}. Consider featuring more acoustic elements in upcoming releases.`,
      detailedExplanation: `Tracks featuring prominent acoustic instruments have 32% higher save rates and 47% more playlist additions compared to your electronic productions ${context}. Listener behavior shows they typically listen to these tracks in full rather than skipping, indicating stronger emotional connection.`
    }
  ];
};

